import close_white from 'images/icons/orders/close_white.svg';
import export_icon_white from 'images/setting_icons/export_icon_white.svg';
import * as React from 'react';
import { ProgressBar } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Translate } from 'react-localize-redux';
import { useDispatch, useSelector } from 'react-redux';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';
import { w3cwebsocket as W3CWebSocket } from 'websocket';
import { AProfile } from '../../atoms/global/users';
import PageLoader, { PageLoaderColors } from '../../components_v2/pageLoader/PageLoader';
import { genFakeDownloadLink, isSuperAdmin } from '../../components_v2/utils';
import { URL_FOUNDATION_WS } from '../../config/keys';
import { get_link } from '../../containers_v2/exports/actions';
import { getLocalStorage } from '../../helpers/localStorage';
import { WebSocketState } from '../../store/reducers/webSocket.reducer';
import { PrimaryColor, RedSidely, SidelyBlack } from '../../styles/global/css/Utils';
import { translateToString } from '../../styles/global/translate';

type MessagePosition = 'first' | 'last' | 'first_and_last' | 'middle'

const ProgressBarStyle = styled.div`
	position: relative;
	.progress .progress-bar {
		background-color: ${PrimaryColor};
		width: 100px;
	}
	.progress {
		width: 100px;
	}
`;

const ToolTip = styled.div<{ isOpen: boolean, isBarOpen: boolean, error: boolean }>`
	position: relative;
	display: inline-block;
	visibility: ${p => !p.isOpen ? 'hidden' : 'visible'};
	width: 250px;
	background-color: ${p => p.error ? RedSidely : PrimaryColor};
	border-radius: 6px;
	top: 35px;
	transform: translateX(-50%);
	position: absolute;
	box-shadow: 3px 1px 7px color-mix(in oklab, ${SidelyBlack} 24%, transparent);
	opacity: ${p => !p.isOpen ? '0' : '1'};
	transition: opacity 0.4s, visibility 0.4s;
	left: ${p => p.isBarOpen ? 50 : 'as'}%;
	z-index: 150;
`;

const ToolTipTextContainer = styled.div<{ disableOver?: boolean, error?: boolean, position?: MessagePosition }>`
	color: white;
	font-size: 12px;
	background-color: ${p => p.error ? RedSidely : ''};
	&:hover {
		background-color: ${p => p.disableOver ? '' : p.error ? RedSidely : PrimaryColor};
	}
	padding: 7px 0px;
	border-radius: ${p => p.position == 'last' ? '0 0 6px 6px' : p.position == 'first' ? '6px 6px 0 0' : p.position == 'first_and_last' ? '6px' : ''};
`;

const ToolTipErrorText = styled.div`
	text-align: center;
	vertical-align: middle;
`;

const ToolTipDate = styled.div`
	display: inline-block;
	width: 20%;
	margin: 0 2.5%;
	vertical-align: middle;
`;
const ToolTipText = styled.div`
	display: inline-block;
	width: 50%;
	margin: 0 2.5%;
	vertical-align: middle;
`;

const ToolTipButton = styled.div`
	display: inline-block;
	width: 5%;
	margin: 0 2.5%;
	vertical-align: middle;
	cursor: pointer;
`;

type LoadingState = 'loading' | 'loaded' | 'error'

export interface WebSocketMessage {
	time: number
	text: string
	fileName?: string
	loadingState: LoadingState
}

// One managed toast follows a server-side export: open for the whole run,
// flipped on completion and closed a while later. Clicking it opens the
// downloads page (settings > exports) in a new tab — the click handler sits
// on the toast itself, since handlers inside the content lose against the
// toast's drag machinery.
const EXPORT_TOAST_ID = 'report-export-progress';

const exportToastContent = (textKey: string) => <div>
	{translateToString(textKey)}{' '}
	<u style={{ cursor: 'pointer', color: PrimaryColor }}>{translateToString('websocket.show_downloads')}</u>
</div>;

const openDownloadsPage = () => window.open('/settings#export', '_blank')?.focus();

const EXPORT_TOAST_OPTIONS = {
	position: 'top-right' as const,
	closeOnClick: false,
	pauseOnHover: true,
	draggable: false,
	onClick: openDownloadsPage,
	style: { cursor: 'pointer' },
};

export function launchExportRunningToast(): void {
	toast.info(exportToastContent('exports.started_notification'), {
		...EXPORT_TOAST_OPTIONS,
		toastId: EXPORT_TOAST_ID,
		autoClose: false,
	});
}

export function failExportRunningToast(): void {
	toast.dismiss(EXPORT_TOAST_ID);
}

function finishExportToast(): void {
	if (toast.isActive(EXPORT_TOAST_ID)) {
		toast.update(EXPORT_TOAST_ID, {
			render: exportToastContent('exports.ready_notification'),
			autoClose: 15000,
		});
	} else {
		toast.info(exportToastContent('exports.ready_notification'), {
			...EXPORT_TOAST_OPTIONS,
			autoClose: 15000,
		});
	}
}

export default function WebSocketListener() {
	const [percentage, setPercentage] = React.useState<number>(NaN);
	const [loadingState, setLoadingState] = React.useState<LoadingState>('loaded');
	const [isConected, setIsConnected] = React.useState<boolean>(false);
	const [isMessagesOpen, setIsMessagesOpen] = React.useState<boolean>(false);
	const ws = React.useRef<W3CWebSocket>(null);
	const dispatch = useDispatch();
	const admin = isSuperAdmin();
	// @ts-expect-error state
	const selector: WebSocketState = useSelector(state => state.webSocket);
	const [messages, setMessages] = React.useState<WebSocketMessage[] | undefined>(selector.messages);
	const firstUpdate = React.useRef(true);
	const profile = useRecoilValue(AProfile);

	React.useEffect(() => {
		function connect() {
			ws.current = new W3CWebSocket(URL_FOUNDATION_WS);
			ws.current.onopen = () => {
				dispatch({ type: 'CONNECTION_STATUS', value: true });
				dispatch({ type: 'JOB_RUNNING', value: Math.floor(Date.now() / 1000) });
				setIsConnected(true);
			};
			ws.current.onclose = () => {
				dispatch({ type: 'CONNECTION_STATUS', value: false });
				if (!isNaN(percentage) && percentage < 1 && percentage > 0) {
					dispatch({ type: 'PERCENTAGE_DECADE', value: NaN });
				}
				setLoadingState('error');
				setIsConnected(false);
				setTimeout(function() {
					connect();
				}, 3000);
			};
			ws.current.onmessage = (message) => {
				const obj = JSON.parse(message.data);
				let perc = NaN;
				for (const [key, value] of Object.entries(obj)) {
					switch (key) {
						case 'percentage':
							perc = value as number;
							dispatch({ type: 'PERCENTAGE', value: perc });
							if (perc == 1) {
								setLoadingState('loaded');
							} else if (loadingState !== 'loading') {
								setLoadingState('loading');
							}
							setPercentage(perc);
							break;
						case 'error':
							dispatch({ type: 'ERROR', value: true });
							setLoadingState('error');
							break;
						case 'rows':
							dispatch({ type: 'FETCHED_ROWS', value });
							break;
						case 'row':
							dispatch({ type: 'FETCHED_ROW', value });
							break;
						case 'is_job_running':
							dispatch({ type: 'JOB_RUNNING', value });
							if (!value) setPercentage(NaN);
							break;
						case 'message': {
							let message = value;
							if (typeof value === 'string' && value.includes('command cannot affect row a second time')) {
								message = {
									text: 'import.duplicate'
								};
							}
							dispatch({
								type: 'INSERT_MESSAGE',
								value: {
									message,
									state: Object.entries(obj).some(([k, v]) => k == 'error' && v == true) ? 'error' : 'loaded'
								}
							});
						}
					}
				}
			};
		}

		connect();
		const wsCurrent = ws.current;
		return () => {
			wsCurrent.close();
			setIsConnected(false);
		};
	}, []);

	React.useEffect(() => {
		if (!ws.current) return;
		if (ws.current && ws.current.readyState === WebSocket.OPEN) {
			ws.current.send(JSON.stringify({
				type: 'subscribe',
				clientId: `${profile.companyId}`
			}));
			// exports are announced to their author alone: the file lands in
			// export/<company>/<user>/ and its download link resolves against
			// the requesting user, so a company-wide message reached
			// colleagues who could not download what it announced
			ws.current.send(JSON.stringify({
				type: 'subscribe',
				clientId: `user:${profile.id}`
			}));
		}
	}, [profile.companyId, profile.id, selector.connected]);

	React.useEffect(() => {
		if (selector.showProgressBar.value) {
			setPercentage(0);
		} else {
			setPercentage(NaN);
		}
	}, [selector.showProgressBar]);

	const toastedExports = React.useRef<Set<number>>(new Set());
	React.useEffect(() => {
		if (firstUpdate.current) {
			firstUpdate.current = false;
			// the persisted history is not news: never toast it
			selector.messages?.forEach(message => toastedExports.current.add(message.time));
			return;
		}

		setMessages(selector.messages);
		setIsMessagesOpen(true);
		// a finished export gets its own toast with the panel link: the panel
		// alone opens in a corner the user is rarely looking at
		for (const message of selector.messages ?? []) {
			if (message.fileName && message.loadingState === 'loaded' && !toastedExports.current.has(message.time)) {
				toastedExports.current.add(message.time);
				finishExportToast();
			}
		}
	}, [selector.messages]);

	const displayMessage = (message: WebSocketMessage, i: number) => {
		let text: string | undefined = message.text ? translateToString(message.text) : undefined;
		if (text?.startsWith('Missing translationId:')) text = message.text;
		return (
			(<ToolTipTextContainer error={message.loadingState == 'error'} position={i == 0 ? (messages?.length == 1 ? 'first_and_last' : 'first') : i + 1 == messages?.length ? 'last' : 'middle'}>
				<ToolTipButton onClick={() => deleteMessage(i, setMessages)}>
					{message.loadingState == 'loading'
						? <PageLoader color={PageLoaderColors.White} size={15} />
						: <img style={{ width: '100%' }} src={close_white} />
					}
				</ToolTipButton>
				<ToolTipDate>{(new Date(message.time)).toLocaleTimeString()}</ToolTipDate>
				<ToolTipText>
					{text}
				</ToolTipText>
				{message.fileName &&
					<ToolTipButton onClick={() => downloadFile(message.fileName)}>
						<img style={{ width: '100%' }} src={export_icon_white} />
					</ToolTipButton>}
			</ToolTipTextContainer>)
		);
	};

	const openMessages = () => setIsMessagesOpen(true);
	const closeMessages = () => setIsMessagesOpen(false);

	const isBarOpen: boolean = !isNaN(percentage) && percentage < 1 && selector.showProgressBar?.value;
	return (
		<ProgressBarStyle onMouseEnter={openMessages} onMouseLeave={closeMessages}>
			{isBarOpen
				? <ProgressBar
					animated={loadingState === 'loading'}
					variant={loadingState === 'error' ? 'danger' : undefined}
					now={percentage}
					min={0}
					max={1}
				/>
				: admin && <span
					className="dot"
					title={translateToString('websocket.show_downloads')}
					onClick={() => setIsMessagesOpen(open => !open)}
					style={{ height: '10px', width: '10px', backgroundColor: !isConected ? RedSidely : PrimaryColor, float: 'right', cursor: 'pointer' }}
				/>
			}
			<ToolTip isOpen={isMessagesOpen} isBarOpen={isBarOpen} onMouseEnter={openMessages} onMouseLeave={closeMessages} error={!isConected}>
				{isConected
					? (messages != null) && (messages.length > 0)
						? <>
							{...messages?.map((m, i) => displayMessage(m, i))}
						</>
						: <ToolTipTextContainer disableOver><ToolTipErrorText><Translate id='websocket.no_messages' /></ToolTipErrorText></ToolTipTextContainer>
					: <ToolTipTextContainer disableOver>
						<ToolTipErrorText>
							<Translate id='websocket.not_connected_to_the_server' />
						</ToolTipErrorText>
					</ToolTipTextContainer>
				}
			</ToolTip>
		</ProgressBarStyle>
	);
}

const downloadFile = (file) => {
	console.log(file);
	const key = file + '.zip';
	get_link(key)
		.then(url => genFakeDownloadLink(url, key))
		.catch(e => console.error(e));
};

const deleteMessage = (i: number, setMessages: (m: WebSocketMessage[]) => void) => {
	const messages: WebSocketMessage[] = getLocalStorage('webSocketMessages') ?? [];
	messages.splice(i, 1);
	localStorage.setItem('webSocketMessages', JSON.stringify(messages));
	setMessages(messages);
};
